// 006 — Epicrisis (Anexo 1 MSP, SNS-MSP/HCU-form.006/2021). Fase 7, Tanda 11: bloques B-J del papel (resumen de cuadro
// clínico, evolución, hallazgos, tratamiento, indicaciones de alta, diagnóstico principal/secundarios/causa externa con
// CIE, condición de alta, médicos tratantes y elaboración/revisión). Campos y decisiones en `bloques006Datos.ts`. El
// `Composition` (LOINC 18842-5) se conserva: es la llave de completitud, calidad HC y de la política de enfermería.
import type { CompositionSection } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { AreaDictado } from './AreaDictado';
import { Bloque, CamposSimples } from './camposSimples';
import type { CampoSimple, Valores } from './camposSimplesDatos';
import { SelectorDiagnosticoCie10 } from './SelectorDiagnosticoCie10';
import { TablaConColumnas } from './uci/TablaConColumnas';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import {
  CAMPOS_CONDICION_006,
  CAMPOS_CUADRO_006,
  CAMPOS_ELABORACION_006,
  CAMPOS_EVOLUCION_006,
  CAMPOS_HALLAZGOS_006,
  CAMPOS_INDICACIONES_006,
  CAMPOS_TRATAMIENTO_006,
  COLUMNAS_MEDICOS_006,
  estadoInicial006,
  FILAS_MEDICOS_006,
  paresDe006,
  seccionesEpicrisis006,
  type Estado006,
} from './bloques006Datos';

function narrativa(texto: string): CompositionSection['text'] {
  const escapado = texto.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br/>');
  return { status: 'generated', div: `<div xmlns="http://www.w3.org/1999/xhtml"><p>${escapado}</p></div>` };
}

type ClaveValores = 'cuadro' | 'evolucion' | 'hallazgos' | 'tratamiento' | 'indicaciones' | 'condicion' | 'elaboracion';

export function Formulario006({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const [estado, setEstado] = useState<Estado006>(() => estadoInicial006());
  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const { guardando, error, guardar } = useGuardado();
  // Servicio del encabezado del PDF (no el 'Consulta externa' por defecto): Epicrisis de egreso hospitalario: el papel no trae servicio; no es consulta externa.
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, '006', t('formularios.f006.titulo'), { servicio: 'Hospitalización' });

  const bloque = (clave: ClaveValores, prefijo: string, lista: readonly CampoSimple[], filas: number): JSX.Element => {
    const valores: Valores = estado[clave];
    const poner = (v: Valores): void => setEstado({ ...estado, [clave]: v });
    const simples = lista.filter((c) => c.tipo !== 'area');
    return (
      <>
        {lista
          .filter((c) => c.tipo === 'area')
          .map((c) => (
            <AreaDictado key={c.clave} id={`f006-${prefijo}-${c.clave}`} etiqueta={c.etiqueta} valor={valores[c.clave] ?? ''} filas={filas} onChange={(v) => poner({ ...valores, [c.clave]: v })} />
          ))}
        {simples.length > 0 && <CamposSimples prefijoId={`f006-${prefijo}`} campos={simples} valores={valores} columnas={3} onChange={(v) => poner({ ...valores, ...v })} />}
      </>
    );
  };

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const profile = medplum.getProfile();
      await medplum.createResource({
        resourceType: 'Composition',
        status: 'final',
        type: { coding: [{ system: 'http://loinc.org', code: '18842-5', display: 'Discharge summary' }] },
        subject,
        encounter,
        date: new Date().toISOString(),
        author: profile ? [{ reference: `${profile.resourceType}/${profile.id}` }] : [],
        title: t('formularios.f006.tituloPorDefecto'),
        section: seccionesEpicrisis006(estado).map((s) => ({ title: s.titulo, text: narrativa(s.texto) })),
      });
      onGuardado(paresDe006(estado), datosEncabezadoMsp);
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{t('formularios.f006.titulo')}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <Bloque titulo={t('formularios.f006.cuadroTitulo')}>{bloque('cuadro', 'cua', CAMPOS_CUADRO_006, 5)}</Bloque>
      <Bloque titulo={t('formularios.f006.evolucionTitulo')}>{bloque('evolucion', 'evo', CAMPOS_EVOLUCION_006, 6)}</Bloque>
      <Bloque titulo={t('formularios.f006.hallazgosTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f006.hallazgosNota')}</p>
        {bloque('hallazgos', 'hal', CAMPOS_HALLAZGOS_006, 5)}
      </Bloque>
      <Bloque titulo={t('formularios.f006.tratamientoTitulo')}>{bloque('tratamiento', 'tra', CAMPOS_TRATAMIENTO_006, 5)}</Bloque>
      <Bloque titulo={t('formularios.f006.indicacionesTitulo')}>{bloque('indicaciones', 'ind', CAMPOS_INDICACIONES_006, 4)}</Bloque>

      <Bloque titulo={t('formularios.f006.diagnosticoTitulo')}>
        <SelectorDiagnosticoCie10 label={t('formularios.f006.principal')} value={estado.principal} onChange={(v) => setEstado({ ...estado, principal: v })} />
        {estado.secundarios.map((d, i) => (
          <SelectorDiagnosticoCie10 key={i} label={`${t('formularios.f006.secundario')} ${i + 1}`} value={d} onChange={(v) => setEstado({ ...estado, secundarios: estado.secundarios.map((x, j) => (j === i ? v : x)) })} />
        ))}
        <p className="text-xs text-hce-muted">{t('formularios.f006.causaExternaNota')}</p>
        <SelectorDiagnosticoCie10 label={t('formularios.f006.causaExterna')} value={estado.causaExterna} onChange={(v) => setEstado({ ...estado, causaExterna: v })} />
      </Bloque>

      <Bloque titulo={t('formularios.f006.condicionTitulo')}>{bloque('condicion', 'con', CAMPOS_CONDICION_006, 2)}</Bloque>

      <Bloque titulo={t('formularios.f006.medicosTitulo')}>
        <TablaConColumnas filas={FILAS_MEDICOS_006} columnas={COLUMNAS_MEDICOS_006} estado={estado.medicos} onChange={(m) => setEstado({ ...estado, medicos: m })} />
      </Bloque>

      <Bloque titulo={t('formularios.f006.profesionalTitulo')}>
        <p className="text-sm text-hce-muted">{t('formularios.profesionalNotaCedula')}</p>
        {bloque('elaboracion', 'ela', CAMPOS_ELABORACION_006, 2)}
      </Bloque>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
